import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Alert,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Snackbar,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Message as MessageIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Add as AddIcon,
  Autorenew as AutorenewIcon,
  Info as InfoIcon,
  AttachFile as AttachFileIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Undo as UndoIcon,
} from "@mui/icons-material";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";
import CronBuilderBox from "../../../components/CronBuilderBox";
import CronStatusPanel from "../../../components/CronStatusPanel";

import { ticketsApi } from "../../../services/api/tickets/tickets";
import {
  getIssueCategoryColor,
  getIssueCategoryLabel,
  TICKET_ISSUE_CATEGORIES,
} from "../../../utils/ticketIssueCategories";

import ticketAiDraftWsService from "../../../services/ws/ticketAiDraft";

const getTicketDraft = (ticketUuid, ticketAIResponses) =>
  (ticketAIResponses[ticketUuid] || [])[0] ?? null;

/** One draft per ticket — show on matching message, or first message if unmatched. */
const getDraftForMessage = (
  ticketUuid,
  message,
  messageIndex,
  ticketMessages,
  ticketAIResponses,
) => {
  const responses = ticketAIResponses[ticketUuid] || [];
  const matched = responses.find(
    (response) => response.message_uuid === message.uuid,
  );
  if (matched) {
    return matched;
  }

  const draft = getTicketDraft(ticketUuid, ticketAIResponses);
  if (!draft) return null;

  if (draft.message_uuid === message.uuid) {
    return draft;
  }

  const messages = getInboundCustomerMessages(ticketMessages[ticketUuid] || []);
  const draftMessageExists = messages.some(
    (m) => m.uuid === draft.message_uuid,
  );
  if (!draftMessageExists && messageIndex === 0) {
    return draft;
  }

  return null;
};

const getMessageFrom = (message) => {
  const from = message?.from;
  if (Array.isArray(from)) {
    return from[0] || {};
  }
  return from || {};
};

/** Shop replies belong in the AI panel, not the customer Messages column. */
const isShopOutboundMessage = (message) => {
  const fromType = String(getMessageFrom(message).type || "").toLowerCase();
  if (fromType === "user") {
    return true;
  }

  const linkedTo = message?.linkedTo || [];
  return linkedTo.some((link) => link?.type === "message" && link?.value);
};

const getInboundCustomerMessages = (messages = []) =>
  messages.filter((message) => !isShopOutboundMessage(message));

const getRecreateConfirmMessage = (existingResponse) => {
  if (existingResponse?.status === "generating") {
    return "Generation is still in progress. Recreate anyway and trigger a new n8n run?";
  }
  if (existingResponse?.status === "error") {
    return "Recreate will retry n8n generation and replace the error state. Continue?";
  }
  return "Recreate will replace the current AI draft with a newly generated response. Continue?";
};

const PANEL_MIN_HEIGHT = 280;
const PANEL_MAX_HEIGHT = 1600;
const VIEWPORT_BOTTOM_GAP = 24;

const computeAiDraftRows = (content) => {
  const lines = (content || "").split(/\r?\n/).length;
  const byChars = Math.ceil((content || "").length / 88);
  return Math.min(24, Math.max(6, Math.max(lines, byChars)));
};

function TicketConversationPanel({ title, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
          bgcolor: "grey.50",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontSize: "0.95rem" }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2 }}>
        {children}
      </Box>
    </Paper>
  );
}

function TicketConversationWorkspace({
  ticketUuid,
  height,
  userResized,
  onHeightChange,
  children,
}) {
  const workspaceRef = useRef(null);
  const dragStateRef = useRef(null);

  const measureAvailableHeight = useCallback(() => {
    if (!workspaceRef.current) return PANEL_MIN_HEIGHT;
    const { top } = workspaceRef.current.getBoundingClientRect();
    return Math.round(
      Math.max(
        PANEL_MIN_HEIGHT,
        window.innerHeight - top - VIEWPORT_BOTTOM_GAP,
      ),
    );
  }, []);

  useLayoutEffect(() => {
    if (userResized) return undefined;

    const syncHeight = () => {
      onHeightChange(ticketUuid, measureAvailableHeight(), false);
    };

    syncHeight();
    const frame = requestAnimationFrame(syncHeight);
    window.addEventListener("resize", syncHeight);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", syncHeight);
    };
  }, [ticketUuid, userResized, measureAvailableHeight, onHeightChange]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;

      const delta = event.clientY - dragState.startY;
      const nextHeight = Math.round(
        Math.min(
          PANEL_MAX_HEIGHT,
          Math.max(PANEL_MIN_HEIGHT, dragState.startHeight + delta),
        ),
      );
      onHeightChange(ticketUuid, nextHeight, true);
    };

    const handleMouseUp = () => {
      dragStateRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [ticketUuid, onHeightChange]);

  const handleResizeStart = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    dragStateRef.current = {
      startY: event.clientY,
      startHeight: height,
    };
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
  };

  return (
    <Box
      ref={workspaceRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        height,
        minHeight: PANEL_MIN_HEIGHT,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>{children}</Box>
      <Box
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize conversation panels"
        onMouseDown={handleResizeStart}
        onWheel={(event) => event.preventDefault()}
        sx={{
          height: 12,
          flexShrink: 0,
          cursor: "ns-resize",
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "grey.100",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          touchAction: "none",
          "&:hover": { bgcolor: "grey.200" },
          "&::after": {
            content: '""',
            width: 44,
            height: 4,
            borderRadius: 999,
            bgcolor: "grey.400",
          },
        }}
      />
    </Box>
  );
}

const TicketsTab = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedTickets, setExpandedTickets] = useState(new Set()); // Track which tickets have messages expanded
  const [ticketMessages, setTicketMessages] = useState({}); // Store messages for each ticket
  const [loadingMessages, setLoadingMessages] = useState(new Set()); // Track which tickets are loading messages
  const [ticketAIResponses, setTicketAIResponses] = useState({}); // Store AI responses for each ticket
  const [generatingWorkflowKeys, setGeneratingWorkflowKeys] = useState(
    new Set(),
  );
  const [loadingAIResponses, setLoadingAIResponses] = useState(new Set());
  const [sendingAIResponses, setSendingAIResponses] = useState(new Set()); // Track which tickets are loading AI responses
  const [loadingAttachments, setLoadingAttachments] = useState(new Set()); // Track which attachments are being downloaded
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const [cronStatus, setCronStatus] = useState(null);
  const [cronExpression, setCronExpression] = useState("0 * * * *");
  const [cronLoading, setCronLoading] = useState(false);
  const [panelHeights, setPanelHeights] = useState({});
  const [userResizedPanels, setUserResizedPanels] = useState(new Set());
  const [closingTickets, setClosingTickets] = useState(new Set());
  const [closeUndo, setCloseUndo] = useState(null);

  // Search parameters
  const [searchParams, setSearchParams] = useState({
    title: "",
    referrer: "All Referrers",
  });

  // Load all tickets on component mount
  useEffect(() => {
    loadTickets();
    loadCronStatus();
  }, []);

  const handlePanelResize = useCallback(
    (ticketUuid, height, fromUser = false) => {
      if (fromUser) {
        setUserResizedPanels((prev) => new Set(prev).add(ticketUuid));
      }

      setPanelHeights((prev) =>
        prev[ticketUuid] === height ? prev : { ...prev, [ticketUuid]: height },
      );
    },
    [],
  );

  const getPanelHeight = useCallback(
    (ticketUuid) => panelHeights[ticketUuid] || PANEL_MIN_HEIGHT,
    [panelHeights],
  );

  const loadCronStatus = async () => {
    try {
      const response = await ticketsApi.getCronStatus();
      const rawData = response.data;
      const normalizedStatus = {
        ...rawData,
        is_active: rawData.active ?? rawData.is_active,
        cron_expression: rawData.expression ?? rawData.cron_expression,
        last_run: rawData.last_run_at ?? rawData.last_run,
        next_run: rawData.next_run_at ?? rawData.next_run,
      };

      setCronStatus(normalizedStatus);

      // Only sync if we have a valid expression
      if (
        normalizedStatus.is_active &&
        normalizedStatus.cron_expression &&
        typeof normalizedStatus.cron_expression === "string"
      ) {
        setCronExpression(normalizedStatus.cron_expression);
      }
    } catch (err) {
      console.error("Failed to load cron status:", err);
    }
  };

  const loadTickets = async (page = 1, title = null, referrer = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ticketsApi.getAllTickets(
        page,
        20,
        title,
        referrer === "All Referrers" ? null : referrer,
      );
      setTickets(response.data.tickets);
      setTotalTickets(response.data.total);
      setTotalPages(response.data.total_pages);
      setCurrentPage(response.data.page);
    } catch (err) {
      setError(
        "Failed to load tickets: " +
          (err.response?.data?.detail || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearchParamChange = (field, value) => {
    setSearchParams((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    loadTickets(1, searchParams.title || null, searchParams.referrer);
  };

  const resetSearchParams = () => {
    setSearchParams({ title: "", referrer: "All Referrers" });
    loadTickets(1);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await ticketsApi.refreshTickets();
      // Reset search params and reload tickets after refresh
      setSearchParams({ title: "", referrer: "All Referrers" });
      await loadTickets(1); // Reset to first page
      // Clear expanded tickets and messages
      setExpandedTickets(new Set());
      setTicketMessages({});
      setLoadingMessages(new Set());
      setTicketAIResponses({});
      setGeneratingWorkflowKeys(new Set());
      setPanelHeights({});
      setUserResizedPanels(new Set());
      setLoadingAIResponses(new Set());
      setError(null); // Clear any previous errors
    } catch (err) {
      setError(
        "Failed to refresh tickets: " +
          (err.response?.data?.detail || err.message),
      );
    } finally {
      setRefreshing(false);
    }
  };

  const handlePageChange = async (event, page) => {
    await loadTickets(page, searchParams.title || null, searchParams.referrer);
  };

  const removeTicketFromLocalState = (ticketUuid) => {
    setTickets((prev) => prev.filter((ticket) => ticket.uuid !== ticketUuid));
    setTotalTickets((prev) => Math.max(0, prev - 1));
    setExpandedTickets((prev) => {
      const newSet = new Set(prev);
      newSet.delete(ticketUuid);
      return newSet;
    });
    setTicketMessages((prev) => {
      const newMessages = { ...prev };
      delete newMessages[ticketUuid];
      return newMessages;
    });
    setLoadingMessages((prev) => {
      const newSet = new Set(prev);
      newSet.delete(ticketUuid);
      return newSet;
    });
    setTicketAIResponses((prev) => {
      const newResponses = { ...prev };
      delete newResponses[ticketUuid];
      return newResponses;
    });
    setPanelHeights((prev) => {
      const next = { ...prev };
      delete next[ticketUuid];
      return next;
    });
    setUserResizedPanels((prev) => {
      const next = new Set(prev);
      next.delete(ticketUuid);
      return next;
    });
  };

  const handleCloseTicket = async (ticket) => {
    const ticketUuid = ticket.uuid;
    if (
      !window.confirm(
        "Close this ticket in Plenty? It will disappear from this list now and be removed from the dashboard on the next ticket refresh.",
      )
    ) {
      return;
    }

    try {
      setClosingTickets((prev) => new Set(prev).add(ticketUuid));
      setError(null);
      await ticketsApi.closeTicket(ticketUuid);
      removeTicketFromLocalState(ticketUuid);
      setCloseUndo({
        ticketUuid,
        title: ticket.title || "Ticket",
        ticket: { ...ticket, is_closed: true, status: "COMPLETED" },
      });
    } catch (err) {
      setError(
        "Failed to close ticket: " +
          (err.response?.data?.detail || err.message),
      );
    } finally {
      setClosingTickets((prev) => {
        const next = new Set(prev);
        next.delete(ticketUuid);
        return next;
      });
    }
  };

  const handleReopenTicket = async (ticketUuid) => {
    const snapshot = closeUndo?.ticket;
    try {
      setError(null);
      await ticketsApi.reopenTicket(ticketUuid);
      setCloseUndo(null);
      if (snapshot) {
        setTickets((prev) => {
          if (prev.some((ticket) => ticket.uuid === ticketUuid)) {
            return prev;
          }
          return [{ ...snapshot, is_closed: false, status: "TODO" }, ...prev];
        });
      }
      await loadTickets(
        currentPage,
        searchParams.title || null,
        searchParams.referrer,
      );
    } catch (err) {
      setError(
        "Failed to reopen ticket: " +
          (err.response?.data?.detail || err.message),
      );
    }
  };

  const handleDeleteTicket = async (ticketUuid) => {
    if (!window.confirm("Are you sure you want to delete this ticket?")) {
      return;
    }

    try {
      await ticketsApi.deleteTicket(ticketUuid);
      removeTicketFromLocalState(ticketUuid);
      setGeneratingWorkflowKeys(new Set());
    } catch (err) {
      setError(
        "Failed to delete ticket: " +
          (err.response?.data?.detail || err.message),
      );
    }
  };

  const handleCategoryChange = async (ticketUuid, messageUuid, aiCategory) => {
    try {
      setError(null);
      const response = await ticketsApi.updateMessageResponseCategory(
        ticketUuid,
        messageUuid,
        { ai_category: aiCategory || null },
      );
      setTicketAIResponses((prev) => {
        const current = prev[ticketUuid] || [];
        const next = current.some((item) => item.message_uuid === messageUuid)
          ? current.map((item) =>
              item.message_uuid === messageUuid ? response.data : item,
            )
          : [...current, response.data];
        return { ...prev, [ticketUuid]: next };
      });
      if (aiCategory) {
        setTickets((prev) =>
          prev.map((ticket) =>
            ticket.uuid === ticketUuid
              ? { ...ticket, ai_category: aiCategory }
              : ticket,
          ),
        );
      }
    } catch (err) {
      setError(
        "Failed to update category: " +
          (err.response?.data?.detail || err.message),
      );
    }
  };

  const handleEditTicket = (ticket) => {
    setEditingTicket(ticket);
    setEditForm({
      title: ticket.title || "",
      status_id: ticket.status_id || "",
      category_id: ticket.category_id || "",
      priority_id: ticket.priority_id || "",
      processed: ticket.processed || false,
      ai_response_generated: ticket.ai_response_generated || false,
      ai_response_content: ticket.ai_response_content || "",
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await ticketsApi.updateTicket(
        editingTicket.uuid,
        editForm,
      );
      setTickets(
        tickets.map((ticket) =>
          ticket.uuid === editingTicket.uuid ? response.data.ticket : ticket,
        ),
      );
      setEditDialogOpen(false);
      setEditingTicket(null);
    } catch (err) {
      setError(
        "Failed to update ticket: " +
          (err.response?.data?.detail || err.message),
      );
    }
  };

  const handleEditAIResponse = (response) => {
    setEditingAIResponse(response);
    setEditAIResponseForm({
      content: response.content,
    });
    setEditAIResponseDialogOpen(true);
  };

  const handleSaveAIResponseEdit = async () => {
    try {
      const response = await ticketsApi.updateAIResponse(
        editingAIResponse.ticket_uuid,
        editAIResponseForm,
      );
      setTicketAIResponses((prev) => ({
        ...prev,
        [editingAIResponse.ticket_uuid]: prev[
          editingAIResponse.ticket_uuid
        ].map((r) =>
          r.message_uuid === editingAIResponse.message_uuid ? response.data : r,
        ),
      }));
      setEditAIResponseDialogOpen(false);
      setEditingAIResponse(null);
    } catch (err) {
      setError(
        "Failed to update AI response: " +
          (err.response?.data?.detail || err.message),
      );
    }
  };

  const applyAiDraftUpdate = useCallback((ticketUuid, draft) => {
    if (!ticketUuid || !draft) {
      return;
    }
    setTicketAIResponses((prev) => {
      const existing = prev[ticketUuid] || [];
      const index = existing.findIndex(
        (entry) => entry.message_uuid === draft.message_uuid,
      );
      const next = [...existing];
      if (index >= 0) {
        next[index] = draft;
      } else if (next.length === 0) {
        next.push(draft);
      } else {
        next[0] = draft;
      }
      return { ...prev, [ticketUuid]: next };
    });
    if (draft.ai_category) {
      setTickets((prev) =>
        prev.map((ticket) =>
          ticket.uuid === ticketUuid
            ? { ...ticket, ai_category: draft.ai_category }
            : ticket,
        ),
      );
    }
  }, []);

  const reloadTicketAIResponses = useCallback(async (ticketUuid) => {
    const response = await ticketsApi.getTicketAIResponses(ticketUuid);
    setTicketAIResponses((prev) => ({
      ...prev,
      [ticketUuid]: response.data,
    }));
    const aiCategory = response.data?.[0]?.ai_category;
    if (aiCategory) {
      setTickets((prev) =>
        prev.map((ticket) =>
          ticket.uuid === ticketUuid
            ? { ...ticket, ai_category: aiCategory }
            : ticket,
        ),
      );
    }
    return response.data;
  }, []);

  const reloadTicketMessages = useCallback(async (ticketUuid) => {
    const response = await ticketsApi.getTicketMessages(ticketUuid);
    setTicketMessages((prev) => ({
      ...prev,
      [ticketUuid]: response.data.messages,
    }));
    return response.data.messages;
  }, []);

  useEffect(() => {
    const unsubscribe = ticketAiDraftWsService.subscribe((message) => {
      applyAiDraftUpdate(message.ticket_uuid, message.draft);
    });
    return unsubscribe;
  }, [applyAiDraftUpdate]);

  const handleSendAIResponse = async (ticketUuid) => {
    const existingResponse = (ticketAIResponses[ticketUuid] || [])[0];
    if (!existingResponse?.content?.trim()) {
      setError("Response content is required before sending to Plenty");
      return;
    }

    try {
      setSendingAIResponses((prev) => new Set(prev).add(ticketUuid));
      setError(null);
      const response = await ticketsApi.sendAIResponse(ticketUuid, {
        content: existingResponse.content,
        recipient: existingResponse.recipient || undefined,
        reference: existingResponse.reference || undefined,
        whisper: existingResponse.whisper ?? false,
      });
      await reloadTicketAIResponses(ticketUuid);
      await reloadTicketMessages(ticketUuid);
      const nextStatus = response.data?.ticket_status || "WAITING_FOR_RESPONSE";
      setTickets((prev) =>
        prev.map((ticket) =>
          ticket.uuid === ticketUuid
            ? {
                ...ticket,
                status: nextStatus,
                ai_response_sent: true,
                processed: true,
              }
            : ticket,
        ),
      );
    } catch (err) {
      setError(
        "Failed to send to Plenty: " +
          (err.response?.data?.detail || err.message),
      );
    } finally {
      setSendingAIResponses((prev) => {
        const next = new Set(prev);
        next.delete(ticketUuid);
        return next;
      });
    }
  };

  const getWorkflowKey = (ticketUuid, messageUuid) =>
    `${ticketUuid}:${messageUuid}`;

  const isWorkflowGenerating = (ticketUuid, messageUuid) =>
    generatingWorkflowKeys.has(getWorkflowKey(ticketUuid, messageUuid));

  const setGeneratingDraftState = (
    ticketUuid,
    messageUuid,
    existingResponse,
  ) => {
    setTicketAIResponses((prev) => {
      const current = prev[ticketUuid] || [];
      if (existingResponse) {
        return {
          ...prev,
          [ticketUuid]: current.map((r) =>
            r.message_uuid === messageUuid
              ? {
                  ...r,
                  status: "generating",
                  content: "Generating response via n8n...",
                }
              : r,
          ),
        };
      }
      return {
        ...prev,
        [ticketUuid]: [
          ...current.filter((r) => r.message_uuid !== messageUuid),
          {
            id: 1,
            ticket_uuid: ticketUuid,
            message_uuid: messageUuid,
            content: "Generating response via n8n...",
            status: "generating",
            recipient: "",
            reference: "",
            whisper: false,
          },
        ],
      };
    });
  };

  const handleTriggerGenerate = async (
    ticketUuid,
    messageUuid,
    { requireConfirm = false } = {},
  ) => {
    const workflowKey = getWorkflowKey(ticketUuid, messageUuid);
    const existingResponse = (ticketAIResponses[ticketUuid] || []).find(
      (r) => r.message_uuid === messageUuid,
    );

    if (requireConfirm) {
      if (!window.confirm(getRecreateConfirmMessage(existingResponse))) {
        return;
      }
    } else if (
      existingResponse?.content?.trim() &&
      existingResponse.status !== "generating" &&
      existingResponse.status !== "error"
    ) {
      if (
        !window.confirm(
          "Recreate will replace the current AI draft with a newly generated response. Continue?",
        )
      ) {
        return;
      }
    }

    try {
      setGeneratingWorkflowKeys((prev) => new Set(prev).add(workflowKey));
      setError(null);
      setGeneratingDraftState(ticketUuid, messageUuid, existingResponse);
      await ticketsApi.triggerGenerateWorkflow(ticketUuid, messageUuid, {
        cachedMessages: ticketMessages[ticketUuid],
      });
    } catch (err) {
      setError(
        "Failed to trigger n8n workflow: " +
          (err.response?.data?.detail || err.message),
      );
      await reloadTicketAIResponses(ticketUuid);
    } finally {
      setGeneratingWorkflowKeys((prev) => {
        const next = new Set(prev);
        next.delete(workflowKey);
        return next;
      });
    }
  };

  const handleCreateAIResponse = async (ticketUuid, messageUuid) => {
    await handleTriggerGenerate(ticketUuid, messageUuid);
  };

  const handleRecreateAIResponse = async (ticketUuid, messageUuid) => {
    await handleTriggerGenerate(ticketUuid, messageUuid, {
      requireConfirm: true,
    });
  };

  const handleDownloadAttachment = async (messageUuid) => {
    setLoadingAttachments((prev) => new Set(prev).add(messageUuid));
    try {
      const response = await ticketsApi.getMessageAttachments(messageUuid);
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `attachments_${messageUuid}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        "Failed to download attachments: " +
          (err.response?.data?.detail || err.message),
      );
    } finally {
      setLoadingAttachments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(messageUuid);
        return newSet;
      });
    }
  };

  const handleStartCron = async () => {
    try {
      setCronLoading(true);
      setError(null);

      // Sanitize cron expression if it's an object or wrapped event
      let scheduleToSend = cronExpression;
      if (typeof cronExpression === "object" && cronExpression !== null) {
        console.warn(
          "Cron expression is an object, attempting to extract string",
          cronExpression,
        );
        scheduleToSend = "0 * * * *";
      }

      await ticketsApi.startCronJob({ cron_expression: scheduleToSend });
      await loadCronStatus();
    } catch (err) {
      setError(
        "Failed to start cron job: " +
          (err.response?.data?.detail || err.message),
      );
    } finally {
      setCronLoading(false);
    }
  };

  const handleStopCron = async () => {
    try {
      setCronLoading(true);
      setError(null);

      await ticketsApi.stopCronJob();
      await loadCronStatus();
    } catch (err) {
      setError(
        "Failed to stop cron job: " +
          (err.response?.data?.detail || err.message),
      );
    } finally {
      setCronLoading(false);
    }
  };

  const handleUpdateCronSchedule = async () => {
    try {
      setCronLoading(true);
      setError(null);

      // Sanitize cron expression
      let scheduleToSend = cronExpression;
      if (typeof cronExpression === "object" && cronExpression !== null) {
        scheduleToSend = "0 * * * *";
      }

      await ticketsApi.updateCronSchedule({ cron_expression: scheduleToSend });
      await loadCronStatus();
    } catch (err) {
      setError(
        "Failed to update cron schedule: " +
          (err.response?.data?.detail || err.message),
      );
    } finally {
      setCronLoading(false);
    }
  };

  const handleLoadMessages = async (ticket) => {
    const ticketUuid = ticket.uuid;
    const isExpanded = expandedTickets.has(ticketUuid);

    if (isExpanded) {
      // Collapse the ticket
      setExpandedTickets((prev) => {
        const newSet = new Set(prev);
        newSet.delete(ticketUuid);
        return newSet;
      });
      setPanelHeights((prev) => {
        const next = { ...prev };
        delete next[ticketUuid];
        return next;
      });
      setUserResizedPanels((prev) => {
        const next = new Set(prev);
        next.delete(ticketUuid);
        return next;
      });
    } else {
      // Expand the ticket
      setExpandedTickets((prev) => new Set(prev).add(ticketUuid));

      // Load messages if not already loaded
      if (!ticketMessages[ticketUuid]) {
        setLoadingMessages((prev) => new Set(prev).add(ticketUuid));
        setError(null);
        try {
          const response = await ticketsApi.getTicketMessages(ticketUuid);
          setTicketMessages((prev) => ({
            ...prev,
            [ticketUuid]: response.data.messages,
          }));
          await reloadTicketAIResponses(ticketUuid);
        } catch (err) {
          setError(
            "Failed to load messages: " +
              (err.response?.data?.detail || err.message),
          );
        } finally {
          setLoadingMessages((prev) => {
            const newSet = new Set(prev);
            newSet.delete(ticketUuid);
            return newSet;
          });
        }
      }

      // Load or refresh AI responses when expanding
      if (!ticketAIResponses[ticketUuid]) {
        setLoadingAIResponses((prev) => new Set(prev).add(ticketUuid));
        setError(null);
        try {
          await reloadTicketAIResponses(ticketUuid);
        } catch (err) {
          setError(
            "Failed to load AI responses: " +
              (err.response?.data?.detail || err.message),
          );
        } finally {
          setLoadingAIResponses((prev) => {
            const newSet = new Set(prev);
            newSet.delete(ticketUuid);
            return newSet;
          });
        }
      } else {
        void reloadTicketAIResponses(ticketUuid);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const getAIResponseStatusColor = (status) => {
    const colors = {
      sent: "success",
      draft: "warning",
      generating: "info",
      sending: "info",
      error: "error",
    };
    return colors[status] || "default";
  };

  const getStatusColorByStatus = (status) => {
    const colors = {
      TODO: "default",
      IN_PROGRESS: "warning",
      WAITING_FOR_RESPONSE: "info",
      COMPLETED: "success",
    };
    return colors[status] || "default";
  };

  const getPriorityColor = (priorityId) => {
    const colors = {
      1: "error", // High
      2: "warning", // Medium
      3: "success", // Low
    };
    return colors[priorityId] || "default";
  };

  const updateResponse = (field, value) => {
    const updated = { ...existingResponse, [field]: value };
    setTicketAIResponses((prev) => ({
      ...prev,
      [ticket.uuid]: prev[ticket.uuid].map((r) =>
        r.id === existingResponse.id ? updated : r,
      ),
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Ticket Management
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Tooltip title="Refresh tickets from PlentyOne API">
            <Button
              variant="contained"
              startIcon={
                refreshing ? <CircularProgress size={20} /> : <RefreshIcon />
              }
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing..." : "Refresh Tickets"}
            </Button>
          </Tooltip>
          <Tooltip
            title={
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  Refresh Process Details:
                </Typography>
                <Typography variant="body2" component="div">
                  • Fetches latest tickets from PlentyOne API
                  <br />
                  • Only considers tickets that are not closed, archived, or
                  deleted
                  <br />
                  • Updates existing tickets and creates new ones that meet
                  criteria
                  <br />
                  <br />
                  <strong>Automatic Deletion Criteria:</strong>
                  <br />
                  • Title contains "ihre auszahlung wurde überwiesen" AND
                  referrer is "ebay" (case-insensitive)
                  <br />
                  • Title contains "ticket id:" AND "[plentyone gmbh] betreff"
                  AND referrer is "help@plentyone.com" or "plentyone.com"
                  (case-insensitive)
                  <br />
                  • Title contains "diese artikel wurden noch nicht verkauft?
                  das sollten wir ändern" AND referrer is "ebay"
                  (case-insensitive)
                  <br />
                  <br />
                  These tickets are automatically removed during refresh.
                </Typography>
              </Box>
            }
          >
            <IconButton size="small" color="info">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Cron Scheduler */}
      <Card sx={{ mb: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              Ticket Refresh Schedule Control
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Schedule automatic ticket refresh using cron expressions. The
              system will run the refresh workflow at specified intervals.
            </Typography>
          </Alert>

          <Grid container spacing={2}>
            {/* Cron Schedule Section */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: "medium" }}
              >
                Automated Refresh Schedule
              </Typography>

              <CronBuilderBox mb={2}>
                <Cron
                  onChange={(e) => {
                    // Some versions of react-cron-generator emit an event object OR a string
                    // We need to handle both
                    const value =
                      typeof e === "string" ? e : e?.target?.value || e;
                    console.log("Cron value changed:", value);
                    setCronExpression(value);
                  }}
                  value={cronExpression}
                  showResultText={true}
                  showResultCron={true}
                />
              </CronBuilderBox>

              <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleStartCron} // This calls updateCronSchedule logic effectively due to API behavior
                  disabled={cronLoading}
                  startIcon={<PlayArrowIcon />}
                >
                  Start / Update Schedule
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleStopCron}
                  disabled={cronLoading || !cronStatus?.is_active}
                  startIcon={<StopIcon />}
                >
                  Stop Schedule
                </Button>
                {cronLoading && <CircularProgress size={20} />}
              </Box>
            </Grid>
          </Grid>

          <CronStatusPanel isActive={cronStatus?.is_active} sx={{ mt: 3 }}>
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))"
              gap={2}
            >
              <Box>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  Cron Status
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {cronStatus?.is_active ? (
                    <Box
                      component="span"
                      sx={{
                        color: "success.main",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <CheckCircleIcon fontSize="small" /> Active
                    </Box>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        color: "error.main",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <StopIcon fontSize="small" /> Inactive
                    </Box>
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  Schedule
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                >
                  {cronStatus?.cron_expression || "Not set"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  Last Run
                </Typography>
                <Typography variant="body2">
                  {cronStatus?.last_run
                    ? new Date(cronStatus.last_run).toLocaleString()
                    : "Never"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                >
                  Next Execution
                </Typography>
                <Typography
                  variant="body2"
                  color={
                    cronStatus?.is_active ? "textPrimary" : "textSecondary"
                  }
                >
                  {cronStatus?.next_run && cronStatus?.is_active
                    ? new Date(cronStatus.next_run).toLocaleString()
                    : "-"}
                </Typography>
              </Box>
            </Box>
          </CronStatusPanel>
        </CardContent>
      </Card>

      {/* Search Tickets */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Search Tickets
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Title"
                value={searchParams.title}
                onChange={(e) =>
                  handleSearchParamChange("title", e.target.value)
                }
                placeholder="Enter ticket title to search"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Referrer</InputLabel>
                <Select
                  value={searchParams.referrer}
                  label="Referrer"
                  onChange={(e) =>
                    handleSearchParamChange("referrer", e.target.value)
                  }
                >
                  <MenuItem value="All Referrers">All Referrers</MenuItem>
                  <MenuItem value="amazon">Amazon</MenuItem>
                  <MenuItem value="otto">Otto</MenuItem>
                  <MenuItem value="ebay">eBay</MenuItem>
                  <MenuItem value="plenty">Plenty</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={
                    loading ? <CircularProgress size={20} /> : <SearchIcon />
                  }
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetSearchParams}
                  disabled={loading}
                >
                  Load All Tickets
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tickets ({totalTickets})
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : tickets.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 3 }}
            >
              No active tickets found. Click "Refresh Tickets" to load from
              PlentyOne.
            </Typography>
          ) : (
            <List>
              {tickets.map((ticket) => (
                <Box key={ticket.uuid}>
                  <ListItem
                    sx={{
                      border: expandedTickets.has(ticket.uuid) ? 2 : 1,
                      borderColor: expandedTickets.has(ticket.uuid)
                        ? "primary.main"
                        : "divider",
                      borderRadius: 1,
                      mb: 1,
                      cursor: "pointer",
                    }}
                    onClick={() => handleLoadMessages(ticket)}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ flexGrow: 1 }}
                            component="span"
                          >
                            {ticket.title}
                          </Typography>
                          <Chip
                            label={ticket.status || "Unknown Status"}
                            color={getStatusColorByStatus(ticket.status)}
                            size="small"
                          />
                          {ticket.ai_category && (
                            <Chip
                              label={getIssueCategoryLabel(ticket.ai_category)}
                              color={getIssueCategoryColor(ticket.ai_category)}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {ticket.priority_id && (
                            <Chip
                              label={`Priority ${ticket.priority_id}`}
                              color={getPriorityColor(ticket.priority_id)}
                              size="small"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                            component="div"
                          >
                            {ticket.conversation_preview ||
                              "No preview available"}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              flexWrap: "wrap",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <PersonIcon fontSize="small" />
                              <Typography variant="caption">
                                {ticket.from_name ||
                                  ticket.from_value ||
                                  "Unknown"}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <ScheduleIcon fontSize="small" />
                              <Typography variant="caption">
                                {formatDate(ticket.created_at)}
                              </Typography>
                            </Box>
                            {ticket.referrer_name && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <EmailIcon fontSize="small" />
                                <Typography variant="caption">
                                  {ticket.referrer_name}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      }
                      secondaryTypographyProps={{ component: "div" }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Tooltip title="Close ticket in Plenty">
                        <span>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloseTicket(ticket);
                            }}
                            color="success"
                            disabled={closingTickets.has(ticket.uuid)}
                          >
                            {closingTickets.has(ticket.uuid) ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CheckCircleIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTicket(ticket);
                        }}
                        color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTicket(ticket.uuid);
                        }}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>

                  {/* Messages and AI Responses for this ticket */}
                  {expandedTickets.has(ticket.uuid) && (
                    <Box sx={{ ml: 2, mr: 2, mb: 2, mt: -1 }}>
                      <Divider sx={{ mb: 2 }} />
                      <TicketConversationWorkspace
                        ticketUuid={ticket.uuid}
                        height={getPanelHeight(ticket.uuid)}
                        userResized={userResizedPanels.has(ticket.uuid)}
                        onHeightChange={handlePanelResize}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            minHeight: 0,
                            width: "100%",
                            display: "flex",
                            flexDirection: { xs: "column", lg: "row" },
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              minHeight: 0,
                              minWidth: 0,
                              display: "flex",
                            }}
                          >
                            <TicketConversationPanel title="Messages">
                              {loadingMessages.has(ticket.uuid) ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    p: 2,
                                  }}
                                >
                                  <CircularProgress size={24} />
                                </Box>
                              ) : getInboundCustomerMessages(
                                  ticketMessages[ticket.uuid] || [],
                                ).length === 0 ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ textAlign: "center", py: 2 }}
                                >
                                  {(ticketMessages[ticket.uuid] || []).length >
                                  0
                                    ? "No customer messages to display."
                                    : "No messages found for this ticket."}
                                </Typography>
                              ) : (
                                <Box>
                                  {getInboundCustomerMessages(
                                    ticketMessages[ticket.uuid] || [],
                                  ).map((message, index) => (
                                    <Box
                                      key={message.uuid || index}
                                      sx={{
                                        mb: 2,
                                        p: 2,
                                        bgcolor: "grey.50",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          mb: 1,
                                        }}
                                      >
                                        <MessageIcon
                                          color="primary"
                                          fontSize="small"
                                        />
                                        <Typography variant="subtitle2">
                                          {message.title ||
                                            `Message ${index + 1}`}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {formatDate(message.createdAt)}
                                          {message.whispered && " (Whispered)"}
                                          {message.forwarded && " (Forwarded)"}
                                        </Typography>
                                      </Box>

                                      {message.message && (
                                        <Box sx={{ mt: 1 }}>
                                          <Typography
                                            variant="body2"
                                            sx={{ mb: 1, fontWeight: "bold" }}
                                          >
                                            Content:
                                          </Typography>
                                          <Box
                                            sx={{
                                              p: 2,
                                              bgcolor: "white",
                                              borderRadius: 1,
                                              border: 1,
                                              borderColor: "grey.200",
                                              "& img": { maxWidth: "100%" },
                                            }}
                                            dangerouslySetInnerHTML={{
                                              __html: message.message,
                                            }}
                                          />
                                        </Box>
                                      )}

                                      <Box
                                        sx={{
                                          display: "flex",
                                          gap: 1,
                                          flexWrap: "wrap",
                                          mt: 1,
                                        }}
                                      >
                                        <Chip
                                          label={`Type: ${
                                            message.messageType || "N/A"
                                          }`}
                                          size="small"
                                        />
                                        <Chip
                                          label={`Status: ${
                                            message.statusId || "N/A"
                                          }`}
                                          size="small"
                                        />
                                        {message.categoryId && (
                                          <Chip
                                            label={`Category: ${message.categoryId}`}
                                            size="small"
                                          />
                                        )}
                                        {message.attachedFilesCount > 0 && (
                                          <Chip
                                            label={
                                              loadingAttachments.has(
                                                message.uuid,
                                              ) ? (
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                  }}
                                                >
                                                  <CircularProgress size={14} />
                                                  <Typography variant="caption">
                                                    Downloading...
                                                  </Typography>
                                                </Box>
                                              ) : (
                                                `Attachments: ${message.attachedFilesCount}`
                                              )
                                            }
                                            size="small"
                                            color="secondary"
                                            clickable={
                                              !loadingAttachments.has(
                                                message.uuid,
                                              )
                                            }
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (
                                                !loadingAttachments.has(
                                                  message.uuid,
                                                )
                                              ) {
                                                handleDownloadAttachment(
                                                  message.uuid,
                                                );
                                              }
                                            }}
                                            sx={{
                                              cursor: loadingAttachments.has(
                                                message.uuid,
                                              )
                                                ? "default"
                                                : "pointer",
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </TicketConversationPanel>
                          </Box>

                          <Box
                            sx={{
                              flex: 1,
                              minHeight: 0,
                              minWidth: 0,
                              display: "flex",
                            }}
                          >
                            <TicketConversationPanel title="AI Responses">
                              {loadingAIResponses.has(ticket.uuid) ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    p: 2,
                                  }}
                                >
                                  <CircularProgress size={24} />
                                </Box>
                              ) : getInboundCustomerMessages(
                                  ticketMessages[ticket.uuid] || [],
                                ).length === 0 ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ textAlign: "center", py: 2 }}
                                >
                                  No messages to respond to.
                                </Typography>
                              ) : (
                                <Box>
                                  {getInboundCustomerMessages(
                                    ticketMessages[ticket.uuid] || [],
                                  ).map((message, index) => {
                                    const existingResponse = getDraftForMessage(
                                      ticket.uuid,
                                      message,
                                      index,
                                      ticketMessages,
                                      ticketAIResponses,
                                    );
                                    const draftMessageUuid =
                                      existingResponse?.message_uuid ||
                                      message.uuid;
                                    const isGenerating = isWorkflowGenerating(
                                      ticket.uuid,
                                      draftMessageUuid,
                                    );
                                    const isAwaitingCallback =
                                      existingResponse?.status === "generating";
                                    const canRecreate =
                                      Boolean(existingResponse);
                                    const showDraftSpinner =
                                      isGenerating || isAwaitingCallback;
                                    const responseLocked =
                                      existingResponse?.status === "sent" ||
                                      existingResponse?.source === "plenty";
                                    return (
                                      <Box
                                        key={message.uuid || index}
                                        sx={{
                                          mb: 2,
                                          p: 2,
                                          bgcolor: existingResponse
                                            ? "blue.50"
                                            : "grey.50",
                                          borderRadius: 1,
                                          border: 1,
                                          borderColor: existingResponse
                                            ? "blue.200"
                                            : "grey.200",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mb: 1,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <MessageIcon
                                              color="primary"
                                              fontSize="small"
                                            />
                                            <Typography variant="subtitle2">
                                              {message.title ||
                                                `Message ${index + 1}`}
                                            </Typography>
                                            {existingResponse ? (
                                              <Chip
                                                label={existingResponse.status}
                                                size="small"
                                                color={getAIResponseStatusColor(
                                                  existingResponse.status,
                                                )}
                                              />
                                            ) : (
                                              <Chip
                                                label="No Response"
                                                size="small"
                                                color="default"
                                              />
                                            )}
                                            {(existingResponse?.ai_category ||
                                              ticket.ai_category) && (
                                              <Chip
                                                label={getIssueCategoryLabel(
                                                  existingResponse?.ai_category ||
                                                    ticket.ai_category,
                                                )}
                                                size="small"
                                                color={getIssueCategoryColor(
                                                  existingResponse?.ai_category ||
                                                    ticket.ai_category,
                                                )}
                                                variant="outlined"
                                              />
                                            )}
                                            {existingResponse?.source ===
                                              "plenty" && (
                                              <Chip
                                                label="Plenty"
                                                size="small"
                                                color="default"
                                                variant="outlined"
                                              />
                                            )}
                                          </Box>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.5,
                                            }}
                                          >
                                            {showDraftSpinner && (
                                              <CircularProgress size={18} />
                                            )}
                                            {canRecreate && (
                                              <Tooltip
                                                title={
                                                  isAwaitingCallback
                                                    ? "Recreate via n8n (generation in progress or stuck)"
                                                    : "Recreate AI response via n8n"
                                                }
                                              >
                                                <IconButton
                                                  size="small"
                                                  color="primary"
                                                  disabled={isGenerating}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRecreateAIResponse(
                                                      ticket.uuid,
                                                      draftMessageUuid,
                                                    );
                                                  }}
                                                >
                                                  <AutorenewIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            {!existingResponse && (
                                              <Tooltip title="Generate AI response via n8n">
                                                <IconButton
                                                  size="small"
                                                  color="secondary"
                                                  disabled={isGenerating}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCreateAIResponse(
                                                      ticket.uuid,
                                                      message.uuid,
                                                    );
                                                  }}
                                                >
                                                  <AddIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </Box>
                                        </Box>

                                        {existingResponse && (
                                          <Box sx={{ mt: 1 }}>
                                            {/* Separate box for Recipient and Reference */}
                                            <Box
                                              sx={{
                                                mb: 2,
                                                p: 2,
                                                bgcolor: "grey.50",
                                                borderRadius: 1,
                                              }}
                                            >
                                              <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                  <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Recipient"
                                                    value={
                                                      existingResponse.recipient ||
                                                      ""
                                                    }
                                                    onChange={(e) => {
                                                      const updated = {
                                                        ...existingResponse,
                                                        recipient:
                                                          e.target.value,
                                                      };
                                                      setTicketAIResponses(
                                                        (prev) => ({
                                                          ...prev,
                                                          [ticket.uuid]: prev[
                                                            ticket.uuid
                                                          ].map((r) =>
                                                            r.message_uuid ===
                                                            existingResponse.message_uuid
                                                              ? updated
                                                              : r,
                                                          ),
                                                        }),
                                                      );
                                                    }}
                                                    disabled={responseLocked}
                                                  />
                                                </Grid>
                                                <Grid item xs={12}>
                                                  <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Reference"
                                                    value={
                                                      existingResponse.reference ||
                                                      ""
                                                    }
                                                    onChange={(e) => {
                                                      const updated = {
                                                        ...existingResponse,
                                                        reference:
                                                          e.target.value,
                                                      };
                                                      setTicketAIResponses(
                                                        (prev) => ({
                                                          ...prev,
                                                          [ticket.uuid]: prev[
                                                            ticket.uuid
                                                          ].map((r) =>
                                                            r.message_uuid ===
                                                            existingResponse.message_uuid
                                                              ? updated
                                                              : r,
                                                          ),
                                                        }),
                                                      );
                                                    }}
                                                    disabled={responseLocked}
                                                  />
                                                </Grid>
                                                <Grid item xs={12}>
                                                  <FormControl
                                                    fullWidth
                                                    size="small"
                                                  >
                                                    <InputLabel>
                                                      Issue category
                                                    </InputLabel>
                                                    <Select
                                                      value={
                                                        existingResponse.ai_category ||
                                                        ticket.ai_category ||
                                                        ""
                                                      }
                                                      label="Issue category"
                                                      onChange={(e) =>
                                                        handleCategoryChange(
                                                          ticket.uuid,
                                                          message.uuid,
                                                          e.target.value,
                                                        )
                                                      }
                                                    >
                                                      <MenuItem value="">
                                                        <em>Not set</em>
                                                      </MenuItem>
                                                      {TICKET_ISSUE_CATEGORIES.map(
                                                        (category) => (
                                                          <MenuItem
                                                            key={category.key}
                                                            value={category.key}
                                                          >
                                                            {category.label}
                                                          </MenuItem>
                                                        ),
                                                      )}
                                                    </Select>
                                                  </FormControl>
                                                </Grid>
                                              </Grid>
                                            </Box>
                                            {/* AI Response Content below */}
                                            <TextField
                                              fullWidth
                                              multiline
                                              minRows={computeAiDraftRows(
                                                existingResponse.content,
                                              )}
                                              size="small"
                                              label="AI Response Content"
                                              value={
                                                existingResponse.content || ""
                                              }
                                              onChange={(e) => {
                                                const updated = {
                                                  ...existingResponse,
                                                  content: e.target.value,
                                                };
                                                setTicketAIResponses(
                                                  (prev) => ({
                                                    ...prev,
                                                    [ticket.uuid]: prev[
                                                      ticket.uuid
                                                    ].map((r) =>
                                                      r.id ===
                                                      existingResponse.id
                                                        ? updated
                                                        : r,
                                                    ),
                                                  }),
                                                );
                                              }}
                                              disabled={
                                                responseLocked ||
                                                isAwaitingCallback ||
                                                isGenerating
                                              }
                                            />
                                            {(
                                              existingResponse.attachments || []
                                            ).length > 0 && (
                                              <Box
                                                sx={{
                                                  mt: 1,
                                                  display: "flex",
                                                  gap: 1,
                                                  flexWrap: "wrap",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  Attachments:
                                                </Typography>
                                                {existingResponse.attachments.map(
                                                  (attachment) => (
                                                    <Chip
                                                      key={attachment.name}
                                                      size="small"
                                                      label={attachment.name}
                                                      variant="outlined"
                                                    />
                                                  ),
                                                )}
                                              </Box>
                                            )}
                                            <Box
                                              sx={{
                                                display: "flex",
                                                gap: 1,
                                                mt: 1,
                                                alignItems: "center",
                                              }}
                                            >
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                startIcon={
                                                  isGenerating ? (
                                                    <CircularProgress
                                                      size={14}
                                                    />
                                                  ) : (
                                                    <AutorenewIcon />
                                                  )
                                                }
                                                onClick={() =>
                                                  handleRecreateAIResponse(
                                                    ticket.uuid,
                                                    draftMessageUuid,
                                                  )
                                                }
                                                disabled={
                                                  responseLocked ||
                                                  existingResponse.status ===
                                                    "sending" ||
                                                  isGenerating
                                                }
                                              >
                                                Recreate via n8n
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<AttachFileIcon />}
                                                onClick={() => {
                                                  // TODO: Implement attachment upload
                                                  alert(
                                                    "Attachment upload not yet implemented",
                                                  );
                                                }}
                                              >
                                                Add Attachment
                                              </Button>
                                              <FormControlLabel
                                                sx={{ ml: 2 }}
                                                control={
                                                  <Switch
                                                    size="small"
                                                    checked={
                                                      existingResponse.whisper ||
                                                      false
                                                    }
                                                    onChange={(e) => {
                                                      const updated = {
                                                        ...existingResponse,
                                                        whisper:
                                                          e.target.checked,
                                                      };
                                                      setTicketAIResponses(
                                                        (prev) => ({
                                                          ...prev,
                                                          [ticket.uuid]: prev[
                                                            ticket.uuid
                                                          ].map((r) =>
                                                            r.id ===
                                                            existingResponse.id
                                                              ? updated
                                                              : r,
                                                          ),
                                                        }),
                                                      );
                                                    }}
                                                  />
                                                }
                                                label="Whisper"
                                              />
                                              <Box sx={{ flexGrow: 1 }} />
                                              <Button
                                                size="small"
                                                variant="contained"
                                                color="success"
                                                startIcon={<SendIcon />}
                                                onClick={() =>
                                                  handleSendAIResponse(
                                                    ticket.uuid,
                                                  )
                                                }
                                                disabled={
                                                  responseLocked ||
                                                  existingResponse.status ===
                                                    "sent" ||
                                                  existingResponse.status ===
                                                    "generating" ||
                                                  sendingAIResponses.has(
                                                    ticket.uuid,
                                                  )
                                                }
                                              >
                                                {sendingAIResponses.has(
                                                  ticket.uuid,
                                                )
                                                  ? "Sending..."
                                                  : "Send to Plenty"}
                                              </Button>
                                            </Box>
                                          </Box>
                                        )}

                                        {!existingResponse && (
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 1 }}
                                          >
                                            Click the + button to trigger the
                                            n8n workflow and generate a draft
                                            reply for this message.
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                            </TicketConversationPanel>
                          </Box>
                        </Box>
                      </TicketConversationWorkspace>
                    </Box>
                  )}
                </Box>
              ))}
            </List>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                mb: 2,
              }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Pagination Info */}
          {totalTickets > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {tickets.length} of {totalTickets} tickets (Page{" "}
                {currentPage} of {totalPages})
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Edit Ticket Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Ticket</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={editForm.title || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, title: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Status ID"
                type="number"
                value={editForm.status_id || ""}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    status_id: parseInt(e.target.value) || null,
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Priority ID"
                type="number"
                value={editForm.priority_id || ""}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    priority_id: parseInt(e.target.value) || null,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="AI Response Content"
                multiline
                rows={4}
                value={editForm.ai_response_content || ""}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    ai_response_content: e.target.value,
                  })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(closeUndo)}
        autoHideDuration={8000}
        onClose={() => setCloseUndo(null)}
        message={
          closeUndo
            ? `"${closeUndo.title}" closed in Plenty (hidden until next refresh removes it)`
            : "Ticket closed"
        }
        action={
          <Button
            color="secondary"
            size="small"
            startIcon={<UndoIcon />}
            onClick={() => handleReopenTicket(closeUndo.ticketUuid)}
          >
            Undo
          </Button>
        }
      />
    </Box>
  );
};

export default TicketsTab;
